import { StockStatus } from '../context/ProductDataStore'
import { isPurchasableStatus } from './stockUtils'

export type ColourwayAvailability = { stockStatus?: string; availableForSale?: boolean | null }
type RankedColourway = {
  _id: string
  rank?: number
  optionTitle?: string
  price?: { unit_amount: number } | null
  stockLimits?: ColourwayAvailability
  isAvailableForSale?: boolean
}

export function rankColourways<T extends RankedColourway>(items: T[]): T[] {
  const rank = (item: T) => typeof item.rank === 'number' && Number.isFinite(item.rank)
    ? item.rank : Number.POSITIVE_INFINITY
  return [...items].sort((a, b) => (rank(a) - rank(b)) || a._id.localeCompare(b._id))
}

export function canSelectColourway(item: RankedColourway, stock: Record<string, ColourwayAvailability> = {}): boolean {
  const availability = stock[item._id] ?? item.stockLimits
  return item.isAvailableForSale !== false
    && Number.isFinite(item.price?.unit_amount) && (item.price?.unit_amount ?? 0) > 0
    && isPurchasableStatus(availability?.stockStatus as StockStatus | undefined, availability?.availableForSale)
}

// Catalogue visibility follows the sale switch, independently of stock status.
// A listed sold-out colourway remains visible, but cannot be selected or bought.
export function canDisplayColourway(item: RankedColourway): boolean {
  return item.isAvailableForSale !== false
    && Number.isFinite(item.price?.unit_amount) && (item.price?.unit_amount ?? 0) > 0
}

export function chooseRankedColourway<T extends RankedColourway>(
  items: T[], requestedId?: string | null, stock: Record<string, ColourwayAvailability> = {}
): T | undefined {
  const available = rankColourways(items).filter(item => canSelectColourway(item, stock))
  // Honour a deliberate variant link, but never open custom colours automatically.
  return available.find(item => item._id === requestedId)
    ?? available.find(item => !item.optionTitle?.toLowerCase().includes('custom'))
}

/**
 * The colourway to show next, or undefined to keep the current one. The colourway in the page link is applied
 * once the list has loaded. While the list is empty (product data reloading, for example after a cache expiry)
 * nothing changes, so the linked colourway is still applied when the list comes back.
 */
export function nextColourway<T extends RankedColourway>(
  items: T[], selectedId: string | null, requestedId: string | null, requestApplied: boolean,
  stock: Record<string, ColourwayAvailability> = {}
): { id: string | null } | undefined {
  if (!items.length) return undefined
  const current = items.find(item => item._id === selectedId)
  if (requestApplied && current && canSelectColourway(current, stock)) return undefined
  return { id: chooseRankedColourway(items, requestApplied ? null : requestedId, stock)?._id ?? null }
}
